// 261030 (acting fallback FE) — cờ ACTING_BLOCKED_SOURCE_ONLY ở cổng 1 (THUẦN: không React/antd).
// BE (`adminFilmWorkflowFourService.actingBlockedFindings`, commit 60d82aa7): video mà AI (Gemini + dự phòng) chặn nội dung được viết cue CHỈ TỪ CÂU NGUỒN để chuỗi đi tiếp;
// status phát cờ ADVISORY (không chặn duyệt) với hai nút: `EDIT_ACTING_MANUAL` (PUT event-shots/:shotId — sửa tay 4 field diễn xuất) và `RETRY_ACTING` (POST …/acting {force:true}).
// File này chỉ biến cờ ⇒ thẻ đọc được + soạn nội dung hộp xác nhận / bản nháp sửa tay / câu kết quả. Việc gọi API nằm ở trang.
import type { ActingBlocked, ActingState } from '../../types/filmActing';
import type { WorkflowFourFinding } from './workflowFourUtils';
import type { RebuildResult } from './actingRebuild';

export const ACTING_BLOCKED_CODE = 'ACTING_BLOCKED_SOURCE_ONLY';
const ACTION_MANUAL = 'EDIT_ACTING_MANUAL';
const ACTION_RETRY = 'RETRY_ACTING';

export const isActingBlockedFlag = (f: WorkflowFourFinding | null | undefined): boolean => !!f && (f.code === ACTING_BLOCKED_CODE || f.actionType === 'ACTING_BLOCKED');

/** Tách cờ diễn xuất bị chặn ra khỏi danh sách cờ chấm (để `flagGroupsOf` không nhóm nhầm chúng vào «AI thấy kịch bản có chỗ cần xem lại»). */
export function splitActingBlocked(findings: WorkflowFourFinding[]): { acting: WorkflowFourFinding[]; rest: WorkflowFourFinding[] } {
  const acting: WorkflowFourFinding[] = []; const rest: WorkflowFourFinding[] = [];
  for (const f of findings) (isActingBlockedFlag(f) ? acting : rest).push(f);
  return { acting, rest };
}

interface EventLike { id: string; eventOrder?: number; shots?: any[] }
const eventIdOfFlag = (f: WorkflowFourFinding): string | null => {
  if (typeof f.eventId === 'string' && f.eventId) return f.eventId;
  return /^event:(.+)$/.exec(String(f.subjectRef ?? ''))?.[1] ?? null;
};
const hasManualShot = (shots: any[]): boolean => shots.some((s) => Array.isArray(s?.actingCueRefs?.manualFields) && s.actingCueRefs.manualFields.length > 0);
const REASON_MAX = 200;

export interface ActingBlockedCard {
  key: string; findingId: string | null; eventId: string | null; eventNumber: number | null;
  title: string; reasonLine: string | null;
  shotCount: number; handWritten: boolean;
  canManual: boolean; canRetry: boolean; retryWhy: string | null;
}

/** Mỗi video MỘT thẻ (cờ trùng video gộp), xếp theo số video. Nút lấy theo `actions[]` BE khai; BE cũ không khai `actions` ⇒ mặc định có cả hai. */
export function actingBlockedCardsOf(flags: WorkflowFourFinding[], events: EventLike[]): ActingBlockedCard[] {
  const seen = new Set<string>(); const out: ActingBlockedCard[] = [];
  for (const f of flags) {
    const eventId = eventIdOfFlag(f);
    const key = eventId ?? f.findingId ?? 'plan';
    if (seen.has(key)) continue; seen.add(key);
    const event = eventId ? events.find((e) => e.id === eventId) : undefined;
    const eventNumber = typeof event?.eventOrder === 'number' ? event.eventOrder : null;
    const shots = Array.isArray(event?.shots) ? event!.shots! : [];
    const types = Array.isArray(f.actions) ? f.actions.map((a) => a?.type) : null;
    const offered = (t: string) => (types ? types.includes(t) : true);
    const handWritten = hasManualShot(shots);
    const reason = typeof f.reason === 'string' ? f.reason.trim() : '';
    const canRetry = offered(ACTION_RETRY) && !handWritten;
    out.push({
      key, findingId: f.findingId ?? null, eventId, eventNumber,
      title: `${eventNumber ? `Video ${eventNumber}` : 'Một video'}: AI từ chối viết diễn xuất — máy dùng nguyên câu trong truyện`,
      reasonLine: reason ? `Máy ghi: ${reason.length > REASON_MAX ? `${reason.slice(0, REASON_MAX - 1).trimEnd()}…` : reason}` : null,
      shotCount: shots.length, handWritten,
      canManual: offered(ACTION_MANUAL) && shots.length > 0,
      canRetry,
      retryWhy: offered(ACTION_RETRY) && handWritten ? 'Video đã có diễn xuất viết tay — máy không ghi đè. Muốn AI viết lại thì gỡ dấu sửa tay trước.' : null,
    });
  }
  return out.sort((a, b) => (a.eventNumber ?? 1e9) - (b.eventNumber ?? 1e9));
}

/** Hộp xác nhận «Thử lại bằng AI»: có tốn tiền AI (không nêu số), khả năng bị từ chối lại, và việc dàn lại shot đi kèm. */
export function actingRetryConfirmOf(eventNumber: number | null): { title: string; lines: string[]; okText: string } {
  return {
    title: `Thử lại diễn xuất bằng AI — ${eventNumber ? `Video ${eventNumber}` : 'video này'}?`,
    lines: [
      'Máy gọi AI viết lại diễn xuất cho riêng video này (có tốn tiền AI).',
      'AI có thể bị từ chối lại: khi đó máy giữ nguyên cue lấy từ truyện và báo bạn — vẫn tốn một lượt gọi.',
      'Nếu AI viết được, máy dàn lại shot của video này theo diễn xuất mới (thêm một lượt AI Đạo diễn).',
    ],
    okText: 'Thử lại bằng AI',
  };
}

/** Trạng thái đưa cho `rebuildByActing`: AI vừa bị chặn lại ⇒ coi như CHƯA viết được để máy KHÔNG dàn lại shot (tốn thêm một lượt AI) trên cue lấy từ nguồn. Không đổi bản gốc. */
export function actingStateForRebuildOf(state: ActingState): ActingState {
  return state.blocked ? { ...state, status: 'NONE' } : state;
}

export function actingRetryResultOf(result: RebuildResult, eventNumber: number | null): { level: 'success' | 'warning'; text: string } | null {
  const name = eventNumber ? `Video ${eventNumber}` : 'video này';
  switch (result.kind) {
    case 'DONE': return { level: 'success', text: `Đã viết lại diễn xuất và dàn lại shot của ${name}.` };
    case 'ACTING_NOT_WRITTEN': return { level: 'warning', text: `AI vẫn từ chối (hoặc chưa viết được) diễn xuất cho ${name} — máy giữ nguyên cue lấy từ truyện. Bạn có thể viết diễn xuất tay, hoặc để nguyên và đi tiếp.` };
    case 'TIMEOUT': return { level: 'warning', text: 'Quá 10 phút chưa xong — tải lại trang để xem việc nền còn chạy không.' };
    case 'JOBS_UNKNOWN': return { level: 'warning', text: 'Chưa kiểm được việc nền đang chạy — bấm Tải lại rồi xem kết quả.' };
    default: return null;
  }
}

/** ── Viết diễn xuất tay: ba field người đọc hiểu được (diễn xuất · chuyển động · đồ vật đổi). `beatKind` là enum kỹ thuật nên không đưa ra. */
export const ACTING_MANUAL_FIELDS = ['performance', 'motion', 'propChange'] as const;
export type ActingManualKey = (typeof ACTING_MANUAL_FIELDS)[number];
export type ActingManualDraft = Record<string, Record<ActingManualKey, string>>;
export const ACTING_MANUAL_LABEL: Record<ActingManualKey, string> = { performance: 'Diễn xuất (nét mặt, cơ thể, giọng)', motion: 'Chuyển động đang diễn ra', propChange: 'Đồ vật đổi trạng thái («đồ vật -> trạng thái sau»)' };

export function actingManualDraftOf(shots: any[]): ActingManualDraft {
  const out: ActingManualDraft = {};
  for (const s of shots) out[s.id] = { performance: String(s.performance ?? ''), motion: String(s.motion ?? ''), propChange: String(s.propChange ?? '') };
  return out;
}
/** Chỉ field THỰC SỰ đổi so với shot; cắt khoảng trắng; xoá trắng ⇒ null. Không đổi gì ⇒ []. */
export function actingManualPatchesOf(shots: any[], draft: ActingManualDraft): Array<{ shotId: string; patch: Partial<Record<ActingManualKey, string | null>> }> {
  const out: Array<{ shotId: string; patch: Partial<Record<ActingManualKey, string | null>> }> = [];
  for (const s of shots) {
    const d = draft[s.id]; if (!d) continue;
    const patch: Partial<Record<ActingManualKey, string | null>> = {};
    for (const k of ACTING_MANUAL_FIELDS) {
      const next = (d[k] ?? '').trim(); const prev = String(s[k] ?? '').trim();
      if (next !== prev) patch[k] = next === '' ? null : next;
    }
    if (Object.keys(patch).length) out.push({ shotId: s.id, patch });
  }
  return out;
}
export function actingManualSaveTextOf(r: { total: number; saved: number; failedShotOrder?: number | null; errorText?: string }): string {
  if (r.total === 0) return 'Chưa sửa gì — không có chữ nào khác bản hiện tại.';
  if (r.saved >= r.total) return `Đã lưu diễn xuất viết tay cho ${r.saved} shot. Máy không ghi đè phần này.`;
  return `Mới lưu ${r.saved}/${r.total} shot${r.failedShotOrder ? ` — dừng ở shot ${r.failedShotOrder}` : ''}${r.errorText ? `: ${r.errorText}` : ''}. Phần đã lưu được giữ; sửa lỗi rồi bấm lưu lại cho phần còn lại.`;
}

/** Nhãn nhỏ «diễn xuất lấy từ nguồn»: `getState.blocked` (nếu đã đọc, kể cả null) thắng `acting_plan.blocked` trong payload plan. */
export function actingSourceOnlyOf(state?: { blocked?: ActingBlocked | null } | null, event?: { actingPlan?: { blocked?: ActingBlocked | null } | null } | null): boolean {
  if (state && 'blocked' in state && state.blocked !== undefined) return !!state.blocked;
  return !!event?.actingPlan?.blocked;
}
